/**
 * Modal para crear/editar nodos de red
 * Incluye configuración avanzada para OLT, NAP, Poste y Cliente
 * @module components/forms/NodeFormModal
 */

import { useState, useEffect, useMemo } from 'react'
import { X, Save, Copy, ChevronDown, ChevronUp, Settings, Folder, Crosshair, Loader2 } from 'lucide-react'
import { NodeType, Coordinates, AnyNode } from '@/types/nodes'
import { useNodeStore, useUIStore, useFolderStore, useFiberStore, useGeoStore } from '@/stores'
import { useGeolocation } from '@/hooks/useGeolocation'
import { NODE_ICONS } from '@/constants'
import { UNFILED_FOLDER_SENTINEL } from '@/constants/folderFilter'
import { OLTConfigForm } from './OLTConfigForm'
import { NAPConfigForm } from './NAPConfigForm'
import { CierreConfigForm } from './CierreConfigForm'
import { PosteConfigForm } from './PosteConfigForm'
import { ClienteConfigForm } from './ClienteConfigForm'
import { IconPicker } from './IconPicker'
import { cableOrigenId, cableDestinoId } from '@/utils/cableEndpoints'
import { syncOltFibersAfterNodeConfigSave } from '@/utils/syncOltFibersToCables'
import { canChangeNodeType } from '@/utils/canChangeNodeType'

interface NodeFormModalProps {
  isOpen: boolean
  onClose: () => void
  nodeType?: NodeType
  coordinates?: Coordinates
  editNode?: AnyNode | null
  duplicateNode?: AnyNode | null
}

export function NodeFormModal({
  isOpen,
  onClose,
  nodeType,
  coordinates,
  editNode,
  duplicateNode,
}: NodeFormModalProps) {
  const addNode = useNodeStore((state) => state.addNode)
  const updateNode = useNodeStore((state) => state.updateNode)
  const addNotification = useUIStore((state) => state.addNotification)
  const folders = useFolderStore((state) => state.folders)
  const activeFolderId = useFolderStore((state) => state.activeFolderId)
  const cables = useFiberStore((state) => state.cables)
  const updateCable = useFiberStore((state) => state.updateCable)

  const isEditing = !!editNode
  const isDuplicating = !!duplicateNode
  const sourceNode = editNode || duplicateNode

  // Obtener cables conectados a este nodo (para NAP y CIERRE)
  const cablesConectados = useMemo(() => {
    if (!sourceNode) return []
    
    return cables
      .filter((cable: any) => {
        const o = cableOrigenId(cable)
        const d = cableDestinoId(cable)
        return o === sourceNode.id || d === sourceNode.id
      })
      .map((cable: any) => ({
        id: cable.id,
        codigo: cable.codigo,
        nombre: cable.nombre || undefined,
        tipo: cable.tipo,
        numeroFibras: cable.numeroFibras,
        tieneBuffers: cable.tieneBuffers,
        buffers: cable.buffers,
        fibras: cable.fibras,
        direccion: cableOrigenId(cable) === sourceNode.id ? 'salida' : 'entrada',
      }))
  }, [cables, sourceNode])

  const canChangeTypeWhenEditing = useMemo(() => {
    if (!isEditing || !editNode) return true
    return canChangeNodeType(editNode, cables)
  }, [isEditing, editNode, cables])

  const typeSelectorDisabled = isEditing && !canChangeTypeWhenEditing

  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    selectedType: (nodeType || 'NAP') as NodeType,
    lat: coordinates?.lat || 0,
    lng: coordinates?.lng || 0,
    folderId: '' as string,
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [showAdvancedConfig, setShowAdvancedConfig] = useState(false)
  const [nodeConfig, setNodeConfig] = useState<Record<string, any>>({})
  const [customIcon, setCustomIcon] = useState<string | null>(null)

  // GPS: lectura puntual + compartir fix con el mapa
  const { getCurrentPosition, loading: gpsLoading, error: gpsError, lastFix } = useGeolocation()
  const setGeoFix = useGeoStore((s) => s.setLastFix)
  const setFollowOnNext = useGeoStore((s) => s.setFollowOnNext)

  const handleUseGPS = async () => {
    try {
      const fix = await getCurrentPosition({ highAccuracy: true, timeoutMs: 20000 })
      setFormData((prev) => ({ ...prev, lat: fix.lat, lng: fix.lng }))
      setErrors((prev) => ({ ...prev, coordinates: '' }))
      setGeoFix(fix)
      setFollowOnNext(true)
      addNotification(
        fix.accuracy > 30 ? 'warning' : 'success',
        fix.accuracy > 30
          ? `Ubicación con baja precisión (±${Math.round(fix.accuracy)} m). Espera unos segundos y reintenta en cielo abierto.`
          : `Ubicación obtenida (±${Math.round(fix.accuracy)} m)`
      )
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo obtener la ubicación'
      addNotification('error', msg)
    }
  }

  // Efecto para inicializar datos cuando el modal se abre
  useEffect(() => {
    if (isOpen) {
      if (sourceNode) {
        // Edición o duplicación: cargar datos del nodo fuente
        setFormData({
          nombre: isDuplicating ? `${sourceNode.nombre || ''} (copia)` : (sourceNode.nombre || ''),
          descripcion: sourceNode.descripcion || '',
          selectedType: sourceNode.tipo, // Usar el tipo del nodo que se está editando
          lat: isDuplicating ? (sourceNode.lat + 0.0001) : sourceNode.lat,
          lng: isDuplicating ? (sourceNode.lng + 0.0001) : sourceNode.lng,
          folderId: (sourceNode as any).folderId || '',
        })
        // Cargar configuración existente
        const existingConfig = (sourceNode as any).config || (sourceNode as any).metadata || {}
        setNodeConfig(existingConfig)
        setCustomIcon((sourceNode as any).customIcon || null)
      } else if (coordinates) {
        // Nuevo nodo: usar coordenadas y tipo especificado
        setFormData({
          nombre: '',
          descripcion: '',
          selectedType: nodeType || 'NAP',
          lat: coordinates.lat,
          lng: coordinates.lng,
          folderId:
            activeFolderId && activeFolderId !== UNFILED_FOLDER_SENTINEL ? activeFolderId : '',
        })
        setNodeConfig({})
        setCustomIcon(null)
      }
      setErrors({})
    }
  }, [isOpen, sourceNode, coordinates, nodeType, isDuplicating, activeFolderId])

  // Efecto para limpiar cuando el modal se cierra
  useEffect(() => {
    if (!isOpen) {
      // Reset al cerrar, pero no afecta cuando está abierto
      setShowAdvancedConfig(false)
    }
  }, [isOpen])

  if (!isOpen) return null

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido'
    } else if (formData.nombre.trim().length < 2) {
      newErrors.nombre = 'El nombre debe tener al menos 2 caracteres'
    }

    if (formData.lat === 0 && formData.lng === 0 && !isEditing) {
      newErrors.coordinates = 'Las coordenadas son requeridas'
    }

    if (formData.lat < -90 || formData.lat > 90) {
      newErrors.lat = 'Latitud inválida (debe estar entre -90 y 90)'
    }

    if (formData.lng < -180 || formData.lng > 180) {
      newErrors.lng = 'Longitud inválida (debe estar entre -180 y 180)'
    }

    setErrors(newErrors)
    
    if (Object.keys(newErrors).length > 0) {
      const errorMessages = Object.values(newErrors).join(', ')
      addNotification('error', `Errores de validación: ${errorMessages}`)
    }
    
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) return

    setLoading(true)
    try {
      const nodeData = {
        tipo: formData.selectedType,
        nombre: formData.nombre,
        descripcion: formData.descripcion,
        lat: formData.lat,
        lng: formData.lng,
        folderId: formData.folderId || null,
        config: nodeConfig,
        customIcon: customIcon,
      } as any

      if (isEditing && editNode) {
        nodeData.id = editNode.id
        const previousConfig = (editNode as { config?: unknown }).config ?? {}
        await updateNode(nodeData)
        if (formData.selectedType === 'POP' || formData.selectedType === 'OLT') {
          try {
            await syncOltFibersAfterNodeConfigSave({
              nodeId: editNode.id,
              previousConfig,
              nextConfig: nodeConfig,
              cables: useFiberStore.getState().cables,
              updateCable,
            })
          } catch (syncErr) {
            console.error(syncErr)
            addNotification(
              'warning',
              'Nodo actualizado; hubo un aviso al sincronizar fibras OLT en cables.'
            )
          }
        }
        addNotification('success', `Nodo "${formData.nombre}" actualizado correctamente`)
      } else {
        const created = await addNode(nodeData as AnyNode)
        if (created && (formData.selectedType === 'POP' || formData.selectedType === 'OLT')) {
          try {
            await syncOltFibersAfterNodeConfigSave({
              nodeId: created.id,
              previousConfig: {},
              nextConfig: nodeConfig,
              cables: useFiberStore.getState().cables,
              updateCable,
            })
          } catch (syncErr) {
            console.error(syncErr)
            addNotification(
              'warning',
              'Nodo creado; hubo un aviso al sincronizar fibras OLT en cables.'
            )
          }
        }
        addNotification('success', `Nodo "${formData.nombre}" creado correctamente`)
      }
      
      onClose()
    } catch (error) {
      console.error('Error al guardar nodo:', error)
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido'
      addNotification('error', `Error al guardar: ${errorMessage}`)
    } finally {
      setLoading(false)
    }
  }

  const getTitle = () => {
    if (isEditing) return `Editar ${formData.selectedType}`
    if (isDuplicating) return `Duplicar ${formData.selectedType}`
    return `Nuevo ${formData.selectedType}`
  }

  const getSubmitText = () => {
    if (loading) return isEditing ? 'Guardando...' : 'Creando...'
    if (isEditing) return 'Guardar Cambios'
    if (isDuplicating) return 'Crear Copia'
    return 'Crear Nodo'
  }

  const getAdvancedConfigLabel = () => {
    switch (formData.selectedType) {
      case 'OLT':
      case 'POP':
        return 'Slots, puertos PON, tecnología'
      case 'NAP':
        return 'Splitters, puertos de salida'
      case 'POSTE':
        return 'Herrajes, tensores, reservorios'
      case 'CLIENTE':
        return 'ONT, WiFi, servicio'
      case 'CIERRE':
        return 'Bandejas, empalmes'
      default:
        return 'Configuración específica'
    }
  }

  const hasAdvancedConfig = ['OLT', 'POP', 'NAP', 'CIERRE', 'POSTE', 'CLIENTE'].includes(formData.selectedType)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-stretch md:items-center justify-center z-[9999] md:p-4">
      <div
        className="bg-white dark:bg-dark-800 md:rounded-lg shadow-2xl w-full max-w-2xl h-full md:h-auto md:max-h-[90vh] overflow-y-auto"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <span className="text-2xl">
              {customIcon || NODE_ICONS[formData.selectedType]}
            </span>
            <div>
              <h2 className="text-xl font-semibold">{getTitle()}</h2>
              {isDuplicating && (
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Duplicando nodo
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg"
            title="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Selector de tipo de nodo */}
          <div>
            <label className="block text-sm font-medium mb-2">Tipo de Nodo</label>
            <div className="grid grid-cols-3 gap-2">
              {(['POP', 'OLT', 'NAP', 'CIERRE', 'CLIENTE', 'POSTE'] as NodeType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, selectedType: type }))
                    setNodeConfig({}) // Reset config al cambiar tipo
                  }}
                  disabled={typeSelectorDisabled}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    formData.selectedType === type
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-dark-700 hover:border-primary-300'
                  } ${typeSelectorDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <div className="text-2xl mb-1">{NODE_ICONS[type]}</div>
                  <div className="text-xs font-medium">{type}</div>
                </button>
              ))}
            </div>
            {isEditing && typeSelectorDisabled && (
              <p className="text-xs text-gray-500 mt-1">
                No se puede cambiar el tipo: hay cables conectados o configuración avanzada (splitters,
                OLTs, puertos ocupados, etc.)
              </p>
            )}
            {isEditing && canChangeTypeWhenEditing && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                Puede cambiar el tipo; al elegir otro se reinicia la configuración avanzada.
              </p>
            )}
          </div>

          {/* Campos comunes */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Nombre *
              </label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-dark-700 dark:border-dark-600 ${
                  errors.nombre ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={`Ej: ${formData.selectedType}-Central-01`}
                disabled={loading}
              />
              {errors.nombre && <p className="text-red-500 text-xs mt-1">{errors.nombre}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Descripción
              </label>
              <textarea
                value={formData.descripcion}
                onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-dark-700 dark:border-dark-600"
                rows={2}
                placeholder="Descripción opcional del nodo"
                disabled={loading}
              />
            </div>

            {/* Selector de Carpeta/Proyecto */}
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <Folder className="w-4 h-4" />
                Carpeta / Proyecto
              </label>
              <select
                value={formData.folderId}
                onChange={(e) => setFormData(prev => ({ ...prev, folderId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-dark-700 dark:border-dark-600"
                disabled={loading}
              >
                <option value="">Sin carpeta (global)</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.parentId ? '└─ ' : ''}{folder.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.folderId 
                  ? 'El nodo pertenecerá a esta carpeta/proyecto'
                  : 'El nodo será visible en todas las vistas'}
              </p>
            </div>

            {/* Coordenadas editables + botón GPS */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">Ubicación</label>
                <button
                  type="button"
                  onClick={handleUseGPS}
                  disabled={loading || gpsLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[40px] rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-60 shadow-sm touch-manipulation"
                  title="Usar la ubicación GPS del dispositivo"
                >
                  {gpsLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Obteniendo…
                    </>
                  ) : (
                    <>
                      <Crosshair className="w-4 h-4" />
                      Usar mi GPS
                    </>
                  )}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Latitud</label>
                  <input
                    type="number"
                    step="any"
                    inputMode="decimal"
                    value={formData.lat}
                    onChange={(e) => setFormData(prev => ({ ...prev, lat: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-dark-700 dark:border-dark-600"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Longitud</label>
                  <input
                    type="number"
                    step="any"
                    inputMode="decimal"
                    value={formData.lng}
                    onChange={(e) => setFormData(prev => ({ ...prev, lng: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-dark-700 dark:border-dark-600"
                    disabled={loading}
                  />
                </div>
              </div>
              {lastFix && (
                <p className={`mt-1.5 text-xs ${lastFix.accuracy > 30 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                  📍 Precisión GPS: ±{Math.round(lastFix.accuracy)} m
                  {lastFix.accuracy > 30 && ' (baja — reintenta en cielo abierto)'}
                </p>
              )}
              {gpsError && (
                <p className="mt-1.5 text-xs text-red-500">{gpsError}</p>
              )}
              {errors.coordinates && <p className="mt-1.5 text-red-500 text-xs">{errors.coordinates}</p>}
            </div>
          </div>

          {/* Icono personalizado */}
          <div>
            <label className="block text-sm font-medium mb-2">Icono Personalizado (opcional)</label>
            <IconPicker
              value={customIcon || undefined}
              onChange={(icon) => setCustomIcon(icon || null)}
              nodeType={formData.selectedType}
            />
          </div>

          {/* Configuración Avanzada */}
          {hasAdvancedConfig && (
            <div className="border border-gray-200 dark:border-dark-700 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowAdvancedConfig(!showAdvancedConfig)}
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  <span className="font-medium">Configuración Avanzada</span>
                  <span className="text-xs text-gray-500">
                    ({getAdvancedConfigLabel()})
                  </span>
                </div>
                {showAdvancedConfig ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>

              {showAdvancedConfig && (
                <div className="p-4 border-t border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800">
                  {(formData.selectedType === 'OLT' || formData.selectedType === 'POP') && (
                    <OLTConfigForm
                      config={nodeConfig as any}
                      onChange={(config) => setNodeConfig(config as Record<string, any>)}
                      cablesConectados={cablesConectados}
                    />
                  )}
                  
                  {formData.selectedType === 'NAP' && (
                    <NAPConfigForm
                      config={nodeConfig as any}
                      onChange={(config) => setNodeConfig(config as Record<string, any>)}
                      cablesConectados={cablesConectados}
                      nodeId={editNode?.id}
                    />
                  )}
                  
                  {formData.selectedType === 'CIERRE' && (
                    <CierreConfigForm
                      config={nodeConfig as any}
                      onChange={(config) => setNodeConfig(config as Record<string, any>)}
                      cablesDisponibles={cablesConectados}
                      nodeId={editNode?.id}
                    />
                  )}
                  
                  {formData.selectedType === 'POSTE' && (
                    <PosteConfigForm
                      config={nodeConfig as any}
                      onChange={(config) => setNodeConfig(config as Record<string, any>)}
                    />
                  )}
                  
                  {formData.selectedType === 'CLIENTE' && (
                    <ClienteConfigForm
                      config={nodeConfig as any}
                      onChange={(config) => setNodeConfig(config as Record<string, any>)}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Botones */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {getSubmitText()}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
